<template>
  <NoteSection
    label="Vitals"
    :required="isPatientAllowedVitalsCollectionAndIsRequired"
    :highlight-required="isSectionIncomplete"
    :form-validated="formValidated"
  >
    <v-col class="col-sm-12">
      <checkbox
        v-model="vitals.is_patient_refused_vitals"
        :label="'Patient Refused Vitals'"
        disable-label-click
      ></checkbox>
    </v-col>
    <help-icon
      title="Vitals"
      body="<p>The information in this section may always be supplied but is only required when the
      patient is marked as being on antibiotics in the Assessment Plan, treated with Amniotic Tissue
      Graft, or when any treated wound has a 'sign of infection' indicated.</p>"
    ></help-icon>
    <text-field
      ref="temp"
      v-model="vitals.temperature"
      :required="isPatientAllowedVitalsCollectionAndIsRequired"
      class="col-sm-6 pb-0"
      label="Temperature"
      numeric
      :mask="tempMask"
      :rules="(vitals.temperature || isPatientAllowedVitalsCollectionAndIsRequired) && dynamicTemperatureChange()"
      :disabled="vitals.is_patient_refused_vitals"
      :hide-details="true"
      @blur="temperatureChanged"
    ></text-field>
    <v-spacer class="col-sm-1 pb-0"></v-spacer>
    <radio-buttons
      v-model="vitals.temperature_scale"
      :items="temperatureScaleOptions"
      :required="(isPatientAllowedVitalsCollectionAndIsRequired || !!vitals.temperature)"
      :disabled="vitals.is_patient_refused_vitals"
      clearable
      class="col-sm-5 pb-0"
    ></radio-buttons>

    <text-field
      v-model="vitals.heart_rate"
      :required="isPatientAllowedVitalsCollectionAndIsRequired"
      class="col-sm-4 pt-6"
      label="Heart Rate"
      placeholder="bpm"
      numeric
      :rules="(vitals.heart_rate|| isPatientAllowedVitalsCollectionAndIsRequired) && heartRateRules"
      :disabled="vitals.is_patient_refused_vitals"
      :mask="tempMask"
    ></text-field>
    <text-field
      v-model="vitals.respiratory_rate"
      :required="isPatientAllowedVitalsCollectionAndIsRequired"
      class="col-sm-4 pt-6"
      label="Respiratory Rate"
      placeholder="cpm"
      numeric
      :rules="(vitals.respiratory_rate|| isPatientAllowedVitalsCollectionAndIsRequired) && respiratoryRateRules"
      :disabled="vitals.is_patient_refused_vitals"
      mask="###"
    ></text-field>
    <v-col
      cols="4"
      class=""
    >
      <v-col
        cols="12"
        class="pa-0 ma-0"
      >
        <div
          class="blood-pressure text-center text-h9 pa-0 ma-0 mb-1"
        >
          Blood Pressure
        </div>
      </v-col>
      <v-col
        cols="12"
        class="d-flex justify-left pa-0"
      >
        <text-field
          v-model="vitals.bp_systolic"
          :required="isPatientAllowedVitalsCollectionAndIsRequired"
          label="Systolic"
          placeholder="mmHg"
          numeric
          :mask="tempMask"
          :rules="(vitals.bp_systolic || isPatientAllowedVitalsCollectionAndIsRequired) && systolicRules"
          :disabled="vitals.is_patient_refused_vitals"
        ></text-field>
        <div
          class="my-2"
        >
          <v-icon>
            {{ icon.mdiSlashForward }}
          </v-icon>
        </div>
        <text-field
          v-model="vitals.bp_diastolic"
          :required="isPatientAllowedVitalsCollectionAndIsRequired"
          label="Diastolic"
          placeholder="mmHg"
          numeric
          :mask="tempMask"
          :rules="(vitals.bp_diastolic || isPatientAllowedVitalsCollectionAndIsRequired) && diastolicRules"
          :disabled="vitals.is_patient_refused_vitals"
        ></text-field>
      </v-col>
    </v-col>
  </NoteSection>
</template>

<script>
import { mdiSlashForward } from '@mdi/js'

export default {
  props: {
    temperature: {
      type: String,
      default: null,
    },
    temperatureScale: {
      type: String,
      default: null,
    },
    heartRate: {
      type: String,
      default: null,
    },
    bpSystolic: {
      type: String,
      default: null,
    },
    bpDiastolic: {
      type: String,
      default: null,
    },
    respiratoryRate: {
      type: String,
      default: null,
    },
    requireVitals: {
      type: Boolean,
      default: null,
    },
    isPatientRefusedVitals: {
      type: Boolean,
      default: false,
    },
    formValidated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icon: {
        mdiSlashForward,
      },

      // Rules
      rules: {
        bloodPressure: this.requireVitals ? 'bloodPressureRequired' : 'bloodPressure',
      },

      // Ranges
      rangeCelsius: {
        min: 11,
        max: 47,
      },
      rangeFahrenheit: {
        min: 53,
        max: 115,
      },
      rangeHeartRate: {
        min: 20,
        max: 250,
      },
      rangeBpDiastolic: {
        min: 20,
        max: 250,
      },
      rangeBpSystolic: {
        min: 20,
        max: 250,
      },
      rangeRespiratoryRate: {
        min: 1,
        max: 60,
      },

      vitals: {
        temperature: this.temperature,
        temperature_scale: this.temperatureScale,
        heart_rate: this.heartRate,
        bp_systolic: this.bpSystolic,
        bp_diastolic: this.bpDiastolic,
        respiratory_rate: this.respiratoryRate,
        is_patient_refused_vitals: this.isPatientRefusedVitals,
      },
      temperatureScaleOptions: [
        { title: 'Celsius', value: 'C' },
        { title: 'Fahrenheit', value: 'F' },
      ],
      tempMask: [/[0-9]/, /[0-9]/, /[0-9|.]/, /[0-9|.]/, /[0-9]/],
    }
  },
  computed: {
    watchedTempScale() {
      return this.vitals.temperature_scale
    },

    // Rules
    heartRateRules() { return `requiredNumberRange(${this.rangeHeartRate.min}, ${this.rangeHeartRate.max})` },
    respiratoryRateRules() { return `requiredNumberRange(${this.rangeRespiratoryRate.min}, ${this.rangeRespiratoryRate.max})` },
    diastolicRules() { return `requiredNumberRange(${this.rangeBpDiastolic.min}, ${this.rangeBpDiastolic.max})` },
    systolicRules() { return `requiredNumberRange(${this.rangeBpSystolic.min},${this.rangeBpSystolic.max})` },
    isPatientAllowedVitalsCollectionAndIsRequired() {
      return !this.vitals.is_patient_refused_vitals && this.requireVitals
    },

    // Evaluates if the Vitals section is incomplete when vitals are required
    isSectionIncomplete() {
      if (!this.isPatientAllowedVitalsCollectionAndIsRequired) return false

      const hasTemp = this.vitals.temperature !== null && this.vitals.temperature !== undefined && String(this.vitals.temperature).trim() !== ''
      const hasTempScale = !!this.vitals.temperature_scale
      const hasHr = this.vitals.heart_rate !== null && this.vitals.heart_rate !== undefined && String(this.vitals.heart_rate).trim() !== ''
      const hasSystolic = this.vitals.bp_systolic !== null && this.vitals.bp_systolic !== undefined && String(this.vitals.bp_systolic).trim() !== ''
      const hasDiastolic = this.vitals.bp_diastolic !== null && this.vitals.bp_diastolic !== undefined && String(this.vitals.bp_diastolic).trim() !== ''
      const hasRr = this.vitals.respiratory_rate !== null && this.vitals.respiratory_rate !== undefined && String(this.vitals.respiratory_rate).trim() !== ''

      return !hasTemp || !hasTempScale || !hasHr || !hasSystolic || !hasDiastolic || !hasRr
    },
  },
  watch: {
    vitals: {
      deep: true,
      handler() {
        this.$emit('update:temperature', this.vitals.temperature)
        this.$emit('update:temperature-scale', this.vitals.temperature_scale)
        this.$emit('update:heart-rate', this.vitals.heart_rate)
        this.$emit('update:bp-systolic', this.vitals.bp_systolic)
        this.$emit('update:bp-diastolic', this.vitals.bp_diastolic)
        this.$emit('update:respiratory-rate', this.vitals.respiratory_rate)
        this.$emit('update:is-patient-refused-vitals', this.vitals.is_patient_refused_vitals)
        if (this.vitals.is_patient_refused_vitals) {
          this.vitals.temperature = null
          this.vitals.temperature_scale = null
          this.vitals.heart_rate = null
          this.vitals.bp_systolic = null
          this.vitals.bp_diastolic = null
          this.vitals.respiratory_rate = null
        }
      },
    },
    watchedTempScale() {
      this.$nextTick(() => {
        this.$refs.temp.validate()
      })
    },
  },
  methods: {
    temperatureChanged() {
      if (this.vitals.temperature >= this.rangeCelsius.min
      && this.vitals.temperature <= this.rangeCelsius.max) {
        this.vitals.temperature_scale = 'C'
      } else if (this.vitals.temperature >= this.rangeFahrenheit.min
      && this.vitals.temperature <= this.rangeFahrenheit.max) this.vitals.temperature_scale = 'F'
      else this.vitals.temperature_scale = null
    },
    dynamicTemperatureChange() {
      if (this.vitals.temperature_scale === 'C') {
        return `requiredNumberRange(${this.rangeCelsius.min}, ${this.rangeCelsius.max})`
      }
      if (this.vitals.temperature_scale === 'F') {
        return `requiredNumberRange(${this.rangeFahrenheit.min}, ${this.rangeFahrenheit.max})`
      }

      return `requiredNumberRange(${this.rangeCelsius.min}, ${this.rangeFahrenheit.max})`
    },
  },
}
</script>

<style lang="scss">
.blood-pressure {
  background-color: #E4E3E5;
  height: 20px;
  width: 100%;
  border-radius: 5px;
  font-weight: 1000 !important;
}
</style>
